import { VisitDto } from '@act/shared/data-transfer-objects';
import {
  createEntityAdapter,
  createSelector,
  createSlice,
  createAction,
} from '@reduxjs/toolkit';
import { visitsRequests } from '../requests/visits.request';
import { useAppSelector } from '../store-hooks';
import { VisitState } from '../types/visitTypes';
import { createApiThunk } from '../utils/api-thunk';
import { ActivitySelectorParams } from '../types/selector-types';
import { getGuideInteractions } from './docketSlice';
import { feedSlice, getFeedItems } from './feedSlice';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const createVisit = createApiThunk(visitsRequests.createVisit);
export const updateVisit = createApiThunk(visitsRequests.updateVisit);
export const deleteVisit = createApiThunk(visitsRequests.deleteVisit);

export const createVisitSuccess = createAction<VisitDto>(
  `visits/createVisitSuccess`
);

export const updateVisitSuccess = createAction<VisitDto>(
  `visits/updateVisitSuccess`
);

// INITIAL STATE ===============================================================
const visitAdapter = createEntityAdapter<VisitDto>();

const initialState: VisitState = {
  visits: visitAdapter.getInitialState({
    status: 'initial',
  }),
};

// SLICE =======================================================================
export const visitsSlice = createSlice({
  name: 'visits',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getFeedItems.fulfilled, (state, action) => {
        visitAdapter.addMany(state.visits, action.payload.visits);
        state.visits.status = 'success';
      })
      .addCase(createVisitSuccess, (state, action) => {
        visitAdapter.addOne(state.visits, action.payload);
        state.visits.status = 'success';
      })
      .addCase(updateVisitSuccess, (state, action) => {
        visitAdapter.upsertOne(state.visits, action.payload);
        state.visits.status = 'success';
      })
      .addCase(feedSlice.actions.feedItemUpdated, (state, action) => {
        if (!action.payload.feedItemModelView.visits) return;

        visitAdapter.upsertMany(
          state.visits,
          action.payload.feedItemModelView.visits
        );
      })
      // Docket Interactions
      .addCase(getGuideInteractions.fulfilled, (state, action) => {
        visitAdapter.upsertMany(state.visits, action.payload.visits);
      });
  },
});

// REDUCER =====================================================================
export const visitsReducer = visitsSlice.reducer;

// SELECTORS ===================================================================
const selectVisitSliceState = state =>
  (state as { visits: typeof initialState })?.visits;

const selectVisitState = createSelector(selectVisitSliceState, s => s.visits);

export const { selectAll: selectVisits, selectEntities: selectVisitsMap } =
  visitAdapter.getSelectors(selectVisitState);

export const selectVisitById = (id: number) =>
  createSelector(selectVisitsMap, visitMap => {
    return visitMap[id];
  });

export const selectVisitsByGuideAndTime: (
  state,
  paramObj: ActivitySelectorParams
) => VisitDto[] = createSelector(
  [
    selectVisits,
    (state, paramObj: ActivitySelectorParams) => paramObj.guideId,
    (state, paramObj: ActivitySelectorParams) => paramObj.startEpochTime,
    (state, paramObj: ActivitySelectorParams) => paramObj.endEpochTime,
  ],
  (visits, guideId, startEpochTime, endEpochTime) => {
    return visits.filter(
      v =>
        v.createdBy === guideId &&
        v.timestamp >= startEpochTime &&
        v.timestamp <= endEpochTime
    );
  }
);

// EXPORT HELPER ===============================================================
export const visitsAppSelectors = {
  useSelectVisitById: id => useAppSelector(selectVisitById(id)),
};

// EFFECTS =====================================================================
// WEBSOCKET ACTIONS ===========================================================
